import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, KanbanSquare, Building2, ShoppingCart, Users, UserCog,
  LogOut, Plus, Trash2, Pencil, ArrowRight, Loader2, Download, Shield, ShieldOff, Sparkles, Printer, FlaskConical,
} from "lucide-react";
import { login, fetchMe, clearToken, getToken, forgotPassword, changePassword } from "../lib/adminAuth";
import * as crm from "../lib/crmApi";
import * as holiday from "../lib/holidayApi";
import { HolidayZones } from "./HolidayZones";
import { Btn, Field, Input, Textarea, Select, Modal, Badge, fmtDate, ORANGE } from "./ui";
import { downloadCsv } from "./csv";

const STAGE_COLOR = {
  New: "slate", Contacted: "blue", Quoted: "purple", "Sample Sent": "orange",
  Discovery: "purple", Won: "green", Lost: "red",
};
const ORDER_COLOR = { Quoted: "slate", Placed: "blue", Shipped: "orange", Closed: "green" };
const HOLIDAY_PRICE_BY_SIZE = { "2' × 3'": 30, "4' × 6'": 120 };
const itemUnitPrice = (it) => (it.unit_price != null ? it.unit_price : (HOLIDAY_PRICE_BY_SIZE[it.size] ?? 0));
const money = (n) => "$" + Number(n || 0).toLocaleString("en-US");

// ------------------------------------------------------------------ Login
function LoginGate({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(email.trim());
        toast.success("If that email is registered, a reset link has been sent.");
        setMode("login");
      } else {
        const user = await login(email.trim(), password);
        onAuthed(user);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (mode === "forgot" ? "Could not send reset link." : "Login failed."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 h-1.5 w-12 rounded-full" style={{ backgroundColor: ORANGE }} />
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">van Gelder CRM</h1>
          <p className="mt-1 text-sm text-slate-500">{mode === "forgot" ? "Reset your password" : "Internal admin — sign in to continue"}</p>
        </div>
        <div className="space-y-4">
          <Field label="Email">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </Field>
          {mode === "login" && (
            <Field label="Password">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </Field>
          )}
          <Btn type="submit" disabled={busy} className="w-full" data-testid="login-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "forgot" ? "Send reset link" : "Sign In")}
          </Btn>
          <button type="button" onClick={() => setMode(mode === "forgot" ? "login" : "forgot")} data-testid="forgot-password-link"
            className="block w-full text-center text-xs font-semibold text-slate-500 hover:text-[#ED8B00]">
            {mode === "forgot" ? "← Back to sign in" : "Forgot your password?"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ForcePasswordChange({ onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match.");
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated. Please sign in again.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-sm rounded-xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 h-1.5 w-12 rounded-full" style={{ backgroundColor: ORANGE }} />
          <h1 className="font-heading text-xl font-extrabold text-slate-900">Update your password</h1>
          <p className="mt-1 text-sm text-slate-500">Your password is over 120 days old. For security, set a new one to continue. It must be at least 80% different and not one of your last 10.</p>
        </div>
        <div className="space-y-4">
          <Field label="Current password"><Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="change-current" /></Field>
          <Field label="New password"><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} data-testid="change-new" /></Field>
          <Field label="Confirm new password"><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} data-testid="change-confirm" /></Field>
          <Btn type="submit" disabled={busy} className="w-full" data-testid="change-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Btn>
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------------ Dashboard
function Dashboard({ reps, canManage }) {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(14);
  const [detail, setDetail] = useState(null);
  const repName = useNameMap(reps);
  const load = useCallback((n) => crm.getDashboard(n).then(setD).catch(() => {}), []);
  useEffect(() => { load(days); }, [load, days]);
  const toggleAuto = async (val) => {
    setD((p) => ({ ...p, auto_convert_samples: val }));
    try { await crm.updateSettings({ auto_convert_samples: val }); toast.success(`Auto-convert ${val ? "on" : "off"}.`); }
    catch { toast.error("Could not update."); load(days); }
  };
  if (!d) return <Loading />;
  const cards = [
    { label: "Companies", value: d.companies, color: "text-blue-600" },
    { label: "Open Inquiries", value: d.open_inquiries, color: "text-orange-600" },
    { label: "Orders", value: d.total_orders, color: "text-emerald-600" },
    { label: "New Submissions", value: d.new_submissions, color: "text-purple-600" },
    { label: "Representatives", value: d.reps, color: "text-slate-700" },
  ];
  return (
    <div data-testid="view-dashboard">
      <PageTitle title="Dashboard" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.label}</p>
            <p className={`mt-2 text-3xl font-extrabold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Rep scoreboard */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="rep-scoreboard">
        <h3 className="font-heading font-bold text-slate-900">Rep Scoreboard</h3>
        {d.rep_scoreboard.length === 0 ? (
          <Empty>No representatives yet.</Empty>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr><Th>Rep</Th><Th>Open pipeline</Th><Th>Open</Th><Th>Won</Th><Th>Lost</Th><Th>Win rate</Th></tr>
            </thead>
            <tbody>
              {d.rep_scoreboard.map((r) => (
                <tr key={r.rep_id} className="border-t border-slate-100" data-testid="scoreboard-row">
                  <Td className="font-semibold text-slate-800">{r.name}</Td>
                  <Td className="font-semibold text-emerald-600">${Number(r.open_value || 0).toLocaleString()}</Td>
                  <Td>{r.open_count}</Td>
                  <Td>{r.won}</Td>
                  <Td>{r.lost}</Td>
                  <Td>{r.win_rate == null ? "—" : `${r.win_rate}%`}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading font-bold text-slate-900">Pipeline by stage</h3>
          <div className="mt-4 space-y-2">
            {d.stages.map((s) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <span><Badge color={STAGE_COLOR[s]}>{s}</Badge></span>
                <span className="font-semibold text-slate-700">{d.inquiries_by_stage[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading font-bold text-slate-900">Orders by status</h3>
          <div className="mt-4 space-y-2">
            {d.order_statuses.map((s) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <span><Badge color={ORDER_COLOR[s]}>{s}</Badge></span>
                <span className="font-semibold text-slate-700">{d.orders_by_status[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Follow-ups */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="followups-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading font-bold text-slate-900">Follow-ups needed</h3>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>No contact in</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="followup-days"
              className="rounded-md border border-slate-300 px-2 py-1 text-sm">
              {[7, 14, 30, 60, 90].map((n) => <option key={n} value={n}>{n} days</option>)}
            </select>
          </div>
        </div>
        {d.followups.length === 0 ? (
          <Empty>All caught up — every active company has recent contact.</Empty>
        ) : (
          <div className="mt-3 divide-y divide-slate-100">
            {d.followups.map((f) => {
              const overdue = f.never_logged || (f.days_since != null && f.days_since >= 30);
              return (
                <button key={f.id} onClick={() => crm.getCompany(f.id).then(setDetail)} data-testid="followup-row"
                  className={`flex w-full items-center justify-between border-l-2 py-2.5 pl-2 text-left hover:bg-slate-50 ${overdue ? "border-red-400" : "border-transparent"}`}>
                  <span>
                    <span className="font-semibold text-slate-800">{f.name}</span>
                    <span className="ml-2 text-xs text-slate-400">{repName(f.rep_id) || "Unassigned"}</span>
                  </span>
                  {f.never_logged
                    ? <Badge color="red">Never contacted</Badge>
                    : (f.days_since >= 30
                        ? <Badge color="red">Overdue · {f.days_since}d</Badge>
                        : <Badge color="orange">{f.days_since}d since contact</Badge>)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Auto-convert rule */}
      {canManage && (
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="autoconvert-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-heading font-bold text-slate-900">Auto-convert sample requests</h3>
            <p className="mt-1 text-sm text-slate-500">When on, every new “Request Samples” submission automatically becomes an inquiry (and company), so nothing slips through the inbox.</p>
          </div>
          <button onClick={() => toggleAuto(!d.auto_convert_samples)} data-testid="autoconvert-toggle"
            className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${d.auto_convert_samples ? "bg-[#ED8B00]" : "bg-slate-300"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${d.auto_convert_samples ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>
      )}

      {detail && <CompanyDetail data={detail} reps={reps} onClose={() => setDetail(null)} onChanged={() => crm.getCompany(detail.company.id).then(setDetail)} refreshOuter={() => load(days)} />}
    </div>
  );
}

// ------------------------------------------------------------------ Inbox
const CUSTOMER_TYPE_LABEL = { new: "New Customer", existing: "Existing Customer" };

const DetailRow = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div className="py-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-800">{value}</p>
    </div>
  );
};

function SubmissionDetail({ sub, onClose, onMarkHandled, onConvert }) {
  return (
    <Modal open onClose={onClose} title="Inquiry details" wide testId="submission-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color="blue">{crm.FORM_TYPES[sub.form_type] || sub.form_type}</Badge>
        <Badge color={sub.status === "handled" ? "green" : "orange"}>{sub.status}</Badge>
        <span className="text-xs text-slate-400">{fmtDate(sub.created_at)}</span>
      </div>
      <div className="mt-3 grid gap-x-6 sm:grid-cols-2">
        <DetailRow label="Name" value={sub.name} />
        <DetailRow label="Email" value={sub.email} />
        <DetailRow label="Phone" value={sub.phone} />
        <DetailRow label="Company" value={sub.company} />
        <DetailRow label="Reason for Contact" value={sub.reason} />
        <DetailRow label="Customer Type" value={CUSTOMER_TYPE_LABEL[sub.customer_type] || sub.customer_type} />
        <DetailRow label="Product Interest" value={sub.product_interest} />
        <DetailRow label="Project Title / Architect" value={sub.project_title} />
        <DetailRow label="Specifier's Location" value={sub.specifiers_location} />
        <DetailRow label="Representative" value={sub.representative} />
        <DetailRow label="Sample Details" value={sub.sample_details} />
        <DetailRow label="Shipping Address" value={sub.shipping_address} />
      </div>
      <DetailRow label="Message" value={sub.message} />
      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        {sub.status !== "handled" && (
          <Btn variant="subtle" onClick={onMarkHandled} data-testid="detail-mark-handled">Mark handled</Btn>
        )}
        <Btn onClick={onConvert} data-testid="detail-convert">Convert to inquiry <ArrowRight className="h-3.5 w-3.5" /></Btn>
      </div>
    </Modal>
  );
}

function InboxView({ reps, companies, refreshLists, onCountChange, kind = "inquiries" }) {
  const isSamples = kind === "samples";
  const [subs, setSubs] = useState(null);
  const [filter, setFilter] = useState(() => localStorage.getItem("vg_crm_inbox_filter") || "all");
  const [convert, setConvert] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(() => crm.listSubmissions().then(setSubs), []);
  useEffect(() => { load(); }, [load]);
  const setFilterMem = (f) => { setFilter(f); localStorage.setItem("vg_crm_inbox_filter", f); };
  if (!subs) return <Loading />;
  const scoped = isSamples
    ? subs.filter((s) => s.form_type === "request_samples")
    : subs.filter((s) => s.form_type !== "request_samples");
  const rows = isSamples || filter === "all" ? scoped : scoped.filter((s) => s.form_type === filter);
  const exportInbox = () => downloadCsv(isSamples ? "vangelder-sample-requests" : "vangelder-inquiries", [
    { key: "Date", get: (s) => fmtDate(s.created_at) },
    { key: "Status", get: (s) => s.status },
    { key: "Type", get: (s) => crm.FORM_TYPES[s.form_type] || s.form_type },
    { key: "Name", get: (s) => s.name },
    { key: "Company", get: (s) => s.company },
    { key: "Email", get: (s) => s.email },
    { key: "Phone", get: (s) => s.phone },
    { key: "Reason", get: (s) => s.reason },
    { key: "Customer Type", get: (s) => s.customer_type },
    { key: "Product Interest", get: (s) => s.product_interest },
    { key: "Sample Details", get: (s) => s.sample_details },
    { key: "Project Title", get: (s) => s.project_title },
    { key: "Specifiers Location", get: (s) => s.specifiers_location },
    { key: "Representative", get: (s) => s.representative },
    { key: "Shipping Address", get: (s) => s.shipping_address },
    { key: "Message", get: (s) => s.message },
  ], rows);
  return (
    <div data-testid={isSamples ? "view-samples" : "view-inbox"}>
      <PageTitle title={isSamples ? "Sample Requests" : "Inquiries"} action={<ExportBtn onClick={exportInbox} disabled={rows.length === 0} testId="inbox-export-csv" />} />
      {!isSamples && (
        <div className="mb-4 flex flex-wrap gap-2">
          {["all", "contact", "request_info"].map((f) => (
            <button key={f} onClick={() => setFilterMem(f)} data-testid={`inbox-filter-${f}`}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold ${filter === f ? "bg-[#ED8B00] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {f === "all" ? "All" : crm.FORM_TYPES[f]}
            </button>
          ))}
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><Th>Name</Th><Th>Company</Th><Th>Type</Th><Th>Interest</Th><Th>Date</Th><Th>Status</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} onClick={() => setView(s)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" data-testid="inbox-row">
                <Td><div className="font-semibold text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.email}</div></Td>
                <Td>{s.company || "—"}</Td>
                <Td><Badge color="blue">{crm.FORM_TYPES[s.form_type] || s.form_type}</Badge></Td>
                <Td>{s.product_interest || "—"}</Td>
                <Td>{fmtDate(s.created_at)}</Td>
                <Td><Badge color={s.status === "handled" ? "green" : "orange"}>{s.status}</Badge></Td>
                <Td>
                  <div className="flex gap-2">
                    {s.status !== "handled" && (
                      <Btn variant="subtle" onClick={async (e) => { e.stopPropagation(); await crm.setSubmissionStatus(s.id, "handled"); load(); onCountChange && onCountChange(); }} data-testid="inbox-mark-handled">Mark handled</Btn>
                    )}
                    <Btn variant="outline" onClick={(e) => { e.stopPropagation(); setConvert(s); }} data-testid="inbox-convert">Convert <ArrowRight className="h-3.5 w-3.5" /></Btn>
                  </div>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td colSpan={7}><Empty>No submissions.</Empty></Td></tr>}
          </tbody>
        </table>
      </div>
      {view && (
        <SubmissionDetail
          sub={view}
          onClose={() => setView(null)}
          onMarkHandled={async () => { await crm.setSubmissionStatus(view.id, "handled"); setView(null); load(); onCountChange && onCountChange(); }}
          onConvert={() => { setConvert(view); setView(null); }}
        />
      )}
      {convert && (
        <ConvertModal sub={convert} reps={reps} companies={companies} onClose={() => setConvert(null)}
          onDone={() => { setConvert(null); load(); refreshLists(); onCountChange && onCountChange(); toast.success("Inquiry created."); }} />
      )}
    </div>
  );
}

function ConvertModal({ sub, reps, companies, onClose, onDone }) {
  const [mode, setMode] = useState(sub.company ? "create" : "none"); // create | existing | none
  const [companyId, setCompanyId] = useState("");
  const [repId, setRepId] = useState("");
  const [pipeline, setPipeline] = useState("standard");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await crm.convertSubmission(sub.id, {
        company_id: mode === "existing" ? companyId : "",
        create_company: mode === "create",
        rep_id: repId, pipeline_type: pipeline,
      });
      onDone();
    } catch (e) {
      toast.error("Could not convert.");
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Convert to inquiry" testId="convert-modal">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">From <b>{sub.name}</b>{sub.company ? ` at ${sub.company}` : ""}.</p>
        <Field label="Company">
          <Select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="convert-company-mode">
            {sub.company && <option value="create">Create / match "{sub.company}"</option>}
            <option value="existing">Link to existing company</option>
            <option value="none">No company</option>
          </Select>
        </Field>
        {mode === "existing" && (
          <Field label="Select company">
            <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">— choose —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
        )}
        <Field label="Representative">
          <Select value={repId} onChange={(e) => setRepId(e.target.value)}>
            <option value="">Unassigned</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
        </Field>
        <Field label="Pipeline">
          <Select value={pipeline} onChange={(e) => setPipeline(e.target.value)}>
            <option value="standard">Standard (New → … → Won/Lost)</option>
            <option value="discovery">Discovery (New → Contacted → Discovery → Won)</option>
          </Select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="subtle" onClick={onClose}>Cancel</Btn>
          <Btn onClick={go} disabled={busy} data-testid="convert-submit">{busy ? "Working…" : "Create inquiry"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ Pipeline
const emptyInquiry = { title: "", company_id: "", location_id: "", rep_id: "", pipeline_type: "standard", stage: "New", value: 0, contact_name: "", contact_email: "", product_interest: "", notes: "" };

function Pipeline({ reps, companies }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const repName = useNameMap(reps);
  const compName = useNameMap(companies);
  const load = useCallback(() => crm.listInquiries().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loading />;
  const cols = crm.STAGES;
  const exportInquiries = () => downloadCsv("vangelder-inquiries", [
    { key: "Title", get: (i) => i.title },
    { key: "Company", get: (i) => compName(i.company_id) },
    { key: "Representative", get: (i) => repName(i.rep_id) },
    { key: "Pipeline", get: (i) => i.pipeline_type },
    { key: "Stage", get: (i) => i.stage },
    { key: "Value", get: (i) => i.value },
    { key: "Product Interest", get: (i) => i.product_interest },
    { key: "Contact Name", get: (i) => i.contact_name },
    { key: "Contact Email", get: (i) => i.contact_email },
    { key: "Created", get: (i) => fmtDate(i.created_at) },
    { key: "Notes", get: (i) => i.notes },
  ], items);
  return (
    <div data-testid="view-pipeline">
      <PageTitle title="Pipeline" action={<div className="flex gap-2"><ExportBtn onClick={exportInquiries} disabled={items.length === 0} testId="pipeline-export-csv" /><Btn onClick={() => setEdit(emptyInquiry)} data-testid="inquiry-new"><Plus className="h-4 w-4" />New inquiry</Btn></div>} />
      <div className="flex gap-4 overflow-x-auto pb-4">
        {cols.map((stage) => {
          const cards = items.filter((i) => i.stage === stage);
          return (
            <div key={stage} className="w-64 flex-shrink-0" data-testid={`pipeline-col-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="mb-2 flex items-center justify-between px-1">
                <Badge color={STAGE_COLOR[stage]}>{stage}</Badge>
                <span className="text-xs font-semibold text-slate-400">{cards.length}</span>
              </div>
              <div className="space-y-2">
                {cards.map((i) => (
                  <button key={i.id} onClick={() => setEdit(i)} data-testid="inquiry-card"
                    className="block w-full rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-[#ED8B00] hover:shadow-sm">
                    <div className="text-sm font-semibold text-slate-800">{i.title}</div>
                    <div className="mt-1 text-xs text-slate-500">{compName(i.company_id) || "No company"}</div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                      <span>{repName(i.rep_id) || "Unassigned"}</span>
                      {i.value ? <span className="font-semibold text-emerald-600">${Number(i.value).toLocaleString()}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {edit && (
        <InquiryModal inquiry={edit} reps={reps} companies={companies}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }} />
      )}
    </div>
  );
}

function InquiryModal({ inquiry, reps, companies, onClose, onSaved }) {
  const isNew = !inquiry.id;
  const [f, setF] = useState({ ...emptyInquiry, ...inquiry });
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (f.company_id) crm.getCompany(f.company_id).then((d) => setLocations(d.locations || [])).catch(() => setLocations([]));
    else setLocations([]);
  }, [f.company_id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title required.");
    setBusy(true);
    try {
      if (isNew) await crm.createInquiry({ ...f, location_id: f.location_id || "" });
      else await crm.updateInquiry(inquiry.id, {
        title: f.title, company_id: f.company_id, location_id: f.location_id || "", rep_id: f.rep_id, pipeline_type: f.pipeline_type,
        stage: f.stage, value: Number(f.value) || 0, notes: f.notes, product_interest: f.product_interest,
      });
      toast.success("Saved."); onSaved();
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };
  const remove = async () => { if (!window.confirm("Delete this inquiry?")) return; await crm.deleteInquiry(inquiry.id); toast.success("Deleted."); onSaved(); };
  return (
    <Modal open onClose={onClose} title={isNew ? "New inquiry" : "Edit inquiry"} wide testId="inquiry-modal">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="Title"><Input value={f.title} onChange={set("title")} data-testid="inquiry-title" /></Field></div>
        <Field label="Company"><Select value={f.company_id} onChange={set("company_id")}><option value="">— none —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
        {locations.length > 0 && (
          <Field label="Store location"><Select value={f.location_id || ""} onChange={set("location_id")} data-testid="inquiry-location">
            <option value="">— company-wide —</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.store_number ? `#${l.store_number} ` : ""}{l.name}</option>)}
          </Select></Field>
        )}
        <Field label="Representative"><Select value={f.rep_id} onChange={set("rep_id")}><option value="">Unassigned</option>{reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></Field>
        <Field label="Pipeline"><Select value={f.pipeline_type} onChange={set("pipeline_type")}><option value="standard">Standard</option><option value="discovery">Discovery</option></Select></Field>
        <Field label="Stage"><Select value={f.stage} onChange={set("stage")} data-testid="inquiry-stage">{crm.STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
        <Field label="Estimated value ($)"><Input type="number" value={f.value} onChange={set("value")} /></Field>
        <Field label="Product interest"><Input value={f.product_interest} onChange={set("product_interest")} /></Field>
        <div className="sm:col-span-2"><Field label="Notes"><Textarea rows={3} value={f.notes} onChange={set("notes")} /></Field></div>
      </div>
      <div className="mt-5 flex justify-between">
        <div>{!isNew && <Btn variant="danger" onClick={remove} data-testid="inquiry-delete"><Trash2 className="h-4 w-4" />Delete</Btn>}</div>
        <div className="flex gap-2"><Btn variant="subtle" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} data-testid="inquiry-save">{busy ? "Saving…" : "Save"}</Btn></div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ Companies
const emptyCompany = { name: "", website: "", email_domain: "", phone: "", address: "", rep_id: "", status: "prospect", notes: "", contacts: [] };

function Companies({ reps, refreshLists }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const repName = useNameMap(reps);
  const load = useCallback(() => crm.listCompanies().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loading />;
  const exportCompanies = () => downloadCsv("vangelder-companies", [
    { key: "Company", get: (c) => c.name },
    { key: "Website", get: (c) => c.website },
    { key: "Email Domain", get: (c) => c.email_domain },
    { key: "Phone", get: (c) => c.phone },
    { key: "Address", get: (c) => c.address },
    { key: "Representative", get: (c) => repName(c.rep_id) },
    { key: "Status", get: (c) => c.status },
    { key: "Open Inquiries", get: (c) => c.open_inquiry_count },
    { key: "Total Inquiries", get: (c) => c.inquiry_count },
    { key: "Orders", get: (c) => c.order_count },
    { key: "Pipeline Value", get: (c) => c.pipeline_value },
    { key: "Last Activity", get: (c) => fmtDate(c.last_activity_at) },
    { key: "Notes", get: (c) => c.notes },
  ], items);
  return (
    <div data-testid="view-companies">
      <PageTitle title="Companies" action={<div className="flex gap-2"><ExportBtn onClick={exportCompanies} disabled={items.length === 0} testId="companies-export-csv" /><Btn onClick={() => setEdit(emptyCompany)} data-testid="company-new"><Plus className="h-4 w-4" />Add company</Btn></div>} />
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><Th>Company</Th><Th>Rep</Th><Th>Status</Th><Th>Inquiries</Th><Th>Orders</Th><Th>Last activity</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid="company-row">
                <Td><button className="font-semibold text-[#ED8B00] hover:underline" onClick={() => crm.getCompany(c.id).then(setDetail)} data-testid="company-open">{c.name}</button><div className="text-xs text-slate-400">{c.address || ""}</div></Td>
                <Td>{repName(c.rep_id) || "—"}</Td>
                <Td><Badge color={c.status === "active" ? "green" : c.status === "inactive" ? "slate" : "orange"}>{c.status}</Badge></Td>
                <Td>{c.open_inquiry_count}/{c.inquiry_count} open</Td>
                <Td>{c.order_count}</Td>
                <Td>{fmtDate(c.last_activity_at)}</Td>
                <Td><Btn variant="subtle" onClick={() => setEdit(c)}><Pencil className="h-3.5 w-3.5" /></Btn></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><Td colSpan={7}><Empty>No companies yet.</Empty></Td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <CompanyModal company={edit} reps={reps} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); refreshLists(); }} />}
      {detail && <CompanyDetail data={detail} reps={reps} onClose={() => setDetail(null)} onChanged={() => crm.getCompany(detail.company.id).then(setDetail)} refreshOuter={load} />}
    </div>
  );
}

function CompanyModal({ company, reps, onClose, onSaved }) {
  const isNew = !company.id;
  const [f, setF] = useState({ ...emptyCompany, ...company, contacts: company.contacts || [] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setContact = (i, k) => (e) => { const cs = [...f.contacts]; cs[i] = { ...cs[i], [k]: e.target.value }; setF({ ...f, contacts: cs }); };
  const addContact = () => setF({ ...f, contacts: [...f.contacts, { name: "", email: "", phone: "", role: "" }] });
  const rmContact = (i) => setF({ ...f, contacts: f.contacts.filter((_, x) => x !== i) });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Company name required.");
    setBusy(true);
    const body = { name: f.name, website: f.website, email_domain: f.email_domain, phone: f.phone, address: f.address, rep_id: f.rep_id, status: f.status, notes: f.notes, contacts: f.contacts };
    try {
      if (isNew) await crm.createCompany(body); else await crm.updateCompany(company.id, body);
      toast.success("Saved."); onSaved();
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };
  const remove = async () => { if (!window.confirm("Delete this company?")) return; await crm.deleteCompany(company.id); toast.success("Deleted."); onSaved(); };
  return (
    <Modal open onClose={onClose} title={isNew ? "Add company" : "Edit company"} wide testId="company-modal">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="Name"><Input value={f.name} onChange={set("name")} data-testid="company-name" /></Field></div>
        <Field label="Website"><Input value={f.website} onChange={set("website")} /></Field>
        <Field label="Email domain"><Input value={f.email_domain} onChange={set("email_domain")} placeholder="acme.com" /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set("phone")} /></Field>
        <Field label="Assigned rep"><Select value={f.rep_id} onChange={set("rep_id")}><option value="">Unassigned</option>{reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></Field>
        <Field label="Status"><Select value={f.status} onChange={set("status")}><option value="prospect">Prospect</option><option value="active">Active</option><option value="inactive">Inactive</option></Select></Field>
        <div className="sm:col-span-2"><Field label="Address"><Input value={f.address} onChange={set("address")} /></Field></div>
        <div className="sm:col-span-2"><Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} /></Field></div>
      </div>
      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contacts</span>
          <Btn variant="subtle" onClick={addContact} data-testid="company-add-contact"><Plus className="h-3.5 w-3.5" />Add contact</Btn>
        </div>
        <div className="space-y-2">
          {f.contacts.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-3" placeholder="Name" value={c.name} onChange={setContact(i, "name")} />
              <Input className="col-span-3" placeholder="Email" value={c.email} onChange={setContact(i, "email")} />
              <Input className="col-span-2" placeholder="Phone" value={c.phone} onChange={setContact(i, "phone")} />
              <Input className="col-span-3" placeholder="Role" value={c.role} onChange={setContact(i, "role")} />
              <button className="col-span-1 text-red-500 hover:text-red-700" onClick={() => rmContact(i)}><Trash2 className="mx-auto h-4 w-4" /></button>
            </div>
          ))}
          {f.contacts.length === 0 && <p className="text-sm text-slate-400">No contacts added.</p>}
        </div>
      </div>
      <div className="mt-5 flex justify-between">
        <div>{!isNew && <Btn variant="danger" onClick={remove}><Trash2 className="h-4 w-4" />Delete</Btn>}</div>
        <div className="flex gap-2"><Btn variant="subtle" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} data-testid="company-save">{busy ? "Saving…" : "Save"}</Btn></div>
      </div>
    </Modal>
  );
}

function CompanyDetail({ data, reps, onClose, onChanged, refreshOuter }) {
  const { company, inquiries, orders, locations = [] } = data;
  const repName = useNameMap(reps);
  const [note, setNote] = useState("");
  const [type, setType] = useState("note");
  const [store, setStore] = useState({ store_number: "", name: "", city: "", state: "", contact_name: "", contact_phone: "" });
  const addNote = async () => {
    if (!note.trim()) return;
    await crm.addActivity(company.id, { type, note });
    setNote(""); onChanged(); refreshOuter();
  };
  const addStore = async (e) => {
    e.preventDefault();
    if (!store.name.trim()) return toast.error("Store name is required.");
    try { await crm.createLocation(company.id, store); setStore({ store_number: "", name: "", city: "", state: "", contact_name: "", contact_phone: "" }); onChanged(); toast.success("Store added."); }
    catch { toast.error("Could not add store."); }
  };
  const delStore = async (id) => { if (!window.confirm("Delete this store?")) return; try { await crm.deleteLocation(id); onChanged(); } catch { toast.error("Delete failed."); } };
  const locCounts = (id) => ({ orders: orders.filter((o) => o.location_id === id).length, inq: inquiries.filter((i) => i.location_id === id).length });
  return (
    <Modal open onClose={onClose} title={company.name} wide testId="company-detail">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Open inquiries" value={company.open_inquiry_count} />
        <Stat label="Total inquiries" value={company.inquiry_count} />
        <Stat label="Orders" value={company.order_count} />
        <Stat label="Pipeline $" value={`$${Number(company.pipeline_value || 0).toLocaleString()}`} />
      </div>
      <div className="mt-3 text-sm text-slate-600">
        <b>Rep:</b> {repName(company.rep_id) || "—"} &nbsp;·&nbsp; <b>Status:</b> {company.status}
        {company.address ? <> &nbsp;·&nbsp; {company.address}</> : null}
      </div>
      {company.contacts?.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase text-slate-500">Contacts</p>
          <div className="mt-1 space-y-1 text-sm">
            {company.contacts.map((c, i) => <div key={i} className="text-slate-700">{c.name} {c.role ? `(${c.role})` : ""} — {c.email} {c.phone}</div>)}
          </div>
        </div>
      )}
      <div className="mt-4" data-testid="company-stores">
        <p className="text-xs font-semibold uppercase text-slate-500">Store Locations ({locations.length})</p>
        <div className="mt-2 space-y-1">
          {locations.length === 0 && <Empty>No store locations yet.</Empty>}
          {locations.map((l) => { const cnt = locCounts(l.id); return (
            <div key={l.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`store-row-${l.id}`}>
              <div>
                <span className="font-semibold text-slate-800">{l.store_number ? `#${l.store_number} · ` : ""}{l.name}</span>
                <span className="text-slate-500">{[l.city, l.state].filter(Boolean).join(", ") ? ` — ${[l.city, l.state].filter(Boolean).join(", ")}` : ""}</span>
                <div className="text-xs text-slate-400">{cnt.orders} order(s) · {cnt.inq} inquiry/sample(s){l.contact_name ? ` · ${l.contact_name}` : ""}{l.contact_phone ? ` ${l.contact_phone}` : ""}</div>
              </div>
              <Btn variant="danger" onClick={() => delStore(l.id)} data-testid={`store-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn>
            </div>
          ); })}
        </div>
        <form onSubmit={addStore} className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-6" data-testid="store-add-form">
          <Input className="sm:col-span-1" placeholder="Store #" value={store.store_number} onChange={(e) => setStore({ ...store, store_number: e.target.value })} data-testid="store-number" />
          <Input className="sm:col-span-2" placeholder="Store name *" value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })} data-testid="store-name" />
          <Input className="sm:col-span-1" placeholder="City" value={store.city} onChange={(e) => setStore({ ...store, city: e.target.value })} data-testid="store-city" />
          <Input className="sm:col-span-1" placeholder="State" value={store.state} onChange={(e) => setStore({ ...store, state: e.target.value })} data-testid="store-state" />
          <Btn type="submit" className="sm:col-span-1" data-testid="store-add"><Plus className="h-4 w-4" />Add</Btn>
        </form>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Inquiries">
          {inquiries.length === 0 ? <Empty>None</Empty> : inquiries.map((i) => (
            <div key={i.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
              <span className="text-slate-700">{i.title}</span><Badge color={STAGE_COLOR[i.stage]}>{i.stage}</Badge>
            </div>
          ))}
        </Panel>
        <Panel title="Orders">
          {orders.length === 0 ? <Empty>None</Empty> : orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
              <span className="text-slate-700">{o.order_number || o.id.slice(0, 8)}</span><Badge color={ORDER_COLOR[o.status]}>{o.status}</Badge>
            </div>
          ))}
        </Panel>
      </div>
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase text-slate-500">Interaction log</p>
        <div className="mt-2 flex gap-2">
          <Select value={type} onChange={(e) => setType(e.target.value)} data-testid="activity-type">
            {["note", "call", "email", "meeting", "sample", "order"].map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log an interaction…" data-testid="activity-note" />
          <Btn onClick={addNote} data-testid="activity-add">Add</Btn>
        </div>
        <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
          {(company.activities || []).slice().reverse().map((a) => (
            <div key={a.id} className="rounded-md bg-slate-50 px-3 py-2 text-sm">
              <div className="flex justify-between"><Badge>{a.type}</Badge><span className="text-xs text-slate-400">{fmtDate(a.at)}</span></div>
              <div className="mt-1 text-slate-700">{a.note}</div>
            </div>
          ))}
          {(company.activities || []).length === 0 && <Empty>No interactions logged.</Empty>}
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ Orders
const emptyOrder = { order_number: "", company_id: "", rep_id: "", status: "Quoted", items: [], total: 0, notes: "" };

function Orders({ reps, companies }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const repName = useNameMap(reps);
  const compName = useNameMap(companies);
  const load = useCallback(() => crm.listOrders().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loading />;
  const exportOrders = () => downloadCsv("vangelder-orders", [
    { key: "Order #", get: (o) => o.order_number || o.id.slice(0, 8) },
    { key: "Company", get: (o) => compName(o.company_id) },
    { key: "Representative", get: (o) => repName(o.rep_id) },
    { key: "Status", get: (o) => o.status },
    { key: "Item Count", get: (o) => (o.items || []).length },
    { key: "Items", get: (o) => (o.items || []).map((i) => `${i.quantity || ""} ${i.unit || ""} ${i.product || ""} ${i.size || ""}`.trim()).join("; ") },
    { key: "Total", get: (o) => o.total },
    { key: "Created", get: (o) => fmtDate(o.created_at) },
    { key: "Notes", get: (o) => o.notes },
  ], items);
  return (
    <div data-testid="view-orders">
      <PageTitle title="Orders" action={<div className="flex gap-2"><ExportBtn onClick={exportOrders} disabled={items.length === 0} testId="orders-export-csv" /><Btn onClick={() => setEdit(emptyOrder)} data-testid="order-new"><Plus className="h-4 w-4" />New order</Btn></div>} />
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><Th>Order #</Th><Th>Company</Th><Th>Rep</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid="order-row">
                <Td className="font-semibold text-slate-800">{o.order_number || o.id.slice(0, 8)}</Td>
                <Td>{compName(o.company_id) || "—"}</Td>
                <Td>{repName(o.rep_id) || "—"}</Td>
                <Td>{(o.items || []).length}</Td>
                <Td>${Number(o.total || 0).toLocaleString()}</Td>
                <Td><Badge color={ORDER_COLOR[o.status]}>{o.status}</Badge></Td>
                <Td><Btn variant="subtle" onClick={() => setEdit(o)}><Pencil className="h-3.5 w-3.5" /></Btn></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><Td colSpan={7}><Empty>No orders yet.</Empty></Td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <OrderModal order={edit} reps={reps} companies={companies} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function OrderModal({ order, reps, companies, onClose, onSaved }) {
  const isNew = !order.id;
  const [f, setF] = useState({ ...emptyOrder, ...order, items: order.items || [] });
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (f.company_id) crm.getCompany(f.company_id).then((d) => setLocations(d.locations || [])).catch(() => setLocations([]));
    else setLocations([]);
  }, [f.company_id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k) => (e) => { const it = [...f.items]; it[i] = { ...it[i], [k]: e.target.value }; setF({ ...f, items: it }); };
  const addItem = () => setF({ ...f, items: [...f.items, { product: "", size: "", quantity: 1, unit: "" }] });
  const rmItem = (i) => setF({ ...f, items: f.items.filter((_, x) => x !== i) });
  const save = async () => {
    setBusy(true);
    const body = { order_number: f.order_number, company_id: f.company_id, location_id: f.location_id || "", rep_id: f.rep_id, status: f.status, total: Number(f.total) || 0, notes: f.notes, items: f.items.map((i) => ({ ...i, quantity: Number(i.quantity) || 0 })) };
    try {
      if (isNew) await crm.createOrder(body); else await crm.updateOrder(order.id, body);
      toast.success("Saved."); onSaved();
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };
  const remove = async () => { if (!window.confirm("Delete this order?")) return; await crm.deleteOrder(order.id); toast.success("Deleted."); onSaved(); };
  return (
    <Modal open onClose={onClose} title={isNew ? "New order" : "Edit order"} wide testId="order-modal">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Order number"><Input value={f.order_number} onChange={set("order_number")} data-testid="order-number" /></Field>
        <Field label="Status"><Select value={f.status} onChange={set("status")} data-testid="order-status">{crm.ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
        <Field label="Company"><Select value={f.company_id} onChange={set("company_id")}><option value="">— none —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
        {locations.length > 0 && (
          <Field label="Store location"><Select value={f.location_id || ""} onChange={set("location_id")} data-testid="order-location">
            <option value="">— company-wide —</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.store_number ? `#${l.store_number} ` : ""}{l.name}</option>)}
          </Select></Field>
        )}
        <Field label="Representative"><Select value={f.rep_id} onChange={set("rep_id")}><option value="">Unassigned</option>{reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select></Field>
        <Field label="Total ($)"><Input type="number" value={f.total} onChange={set("total")} /></Field>
      </div>
      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Line items</span>
          <Btn variant="subtle" onClick={addItem} data-testid="order-add-item"><Plus className="h-3.5 w-3.5" />Add item</Btn>
        </div>
        <div className="space-y-2">
          {f.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-5" placeholder="Product" value={it.product} onChange={setItem(i, "product")} />
              <Input className="col-span-3" placeholder="Size" value={it.size} onChange={setItem(i, "size")} />
              <Input className="col-span-2" placeholder="Qty" type="number" value={it.quantity} onChange={setItem(i, "quantity")} />
              <Input className="col-span-1" placeholder="Unit" value={it.unit} onChange={setItem(i, "unit")} />
              <button className="col-span-1 text-red-500 hover:text-red-700" onClick={() => rmItem(i)}><Trash2 className="mx-auto h-4 w-4" /></button>
            </div>
          ))}
          {f.items.length === 0 && <p className="text-sm text-slate-400">No line items.</p>}
        </div>
      </div>
      <div className="mt-3"><Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} /></Field></div>
      <div className="mt-5 flex justify-between">
        <div>{!isNew && <Btn variant="danger" onClick={remove}><Trash2 className="h-4 w-4" />Delete</Btn>}</div>
        <div className="flex gap-2"><Btn variant="subtle" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} data-testid="order-save">{busy ? "Saving…" : "Save"}</Btn></div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ Holiday Mat Program
const HOLIDAY_COLOR = {
  New: "orange", "Artwork Review": "purple", "Awaiting Customer": "red",
  "Production Ready": "blue", "In Production": "blue", Completed: "green",
  "On Hold": "slate", Cancelled: "slate",
};
const EMAIL_COLOR = { sent: "green", failed: "red", pending: "slate" };

function HolidayOrders({ canManage }) {
  const [orders, setOrders] = useState(null);
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(null);
  const [tab, setTab] = useState("requests");
  const load = useCallback(() => {
    holiday.listHolidayOrders().then(setOrders).catch(() => setOrders([]));
    holiday.listHolidayCollections().then(setMeta).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!orders) return <Loading />;

  const program = meta?.program;
  const activeColl = (meta?.collections || []).find((c) => c.id === program?.active_collection_id);
  const toggleProgram = async (val) => {
    setMeta((m) => ({ ...m, program: { ...m.program, active: val } }));
    try { await holiday.updateHolidayProgram({ active: val }); toast.success(`Program ${val ? "activated" : "deactivated"}.`); }
    catch { toast.error("Could not update program."); load(); }
  };
  const refreshView = (updated) => { setView(updated); load(); };
  const exportOrders = () => downloadCsv("vangelder-holiday-requests", [
    { key: "Request", get: (o) => o.reference },
    { key: "Date", get: (o) => fmtDate(o.submitted_at || o.created_at) },
    { key: "Status", get: (o) => o.status },
    { key: "Assigned", get: (o) => o.assigned_to },
    { key: "Waiting On", get: (o) => o.waiting_on },
    { key: "Company", get: (o) => o.customer?.company },
    { key: "Contact", get: (o) => o.customer?.contact_name },
    { key: "Email", get: (o) => o.customer?.email },
    { key: "Phone", get: (o) => o.customer?.phone },
    { key: "Billing", get: (o) => o.customer?.billing_address },
    { key: "Shipping", get: (o) => o.customer?.ship_same ? "Same as billing" : o.customer?.shipping_address },
    { key: "Needed By", get: (o) => o.customer?.needed_by_date },
    { key: "Total Mats", get: (o) => o.total_mats },
    { key: "Designs", get: (o) => (o.items || []).map((i) => `${i.quantity}x ${i.design_name} (${i.size})${i.personalization?.enabled ? " [personalized]" : ""}`).join("; ") },
    { key: "Personalization Types", get: (o) => (o.items || []).filter((i) => i.personalization?.enabled).map((i) => i.personalization.type || "Unspecified").join("; ") },
    { key: "Notes", get: (o) => o.customer?.notes },
  ], orders);

  return (
    <div data-testid="view-holiday">
      <PageTitle title="Holiday Mat Program" action={tab === "requests" ? <ExportBtn onClick={exportOrders} disabled={orders.length === 0} testId="holiday-export-csv" /> : null} />

      <div className="mb-6 flex gap-2 border-b border-slate-200" data-testid="holiday-subtabs">
        {[["requests", "Requests"], ["reporting", "Reporting"]].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`holiday-tab-${v}`}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === v ? "border-[#ED8B00] text-[#ED8B00]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "reporting" ? <HolidayReporting /> : (
      <>
      {program && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="holiday-program-card">
          <div>
            <h3 className="font-heading font-bold text-slate-900">Program status</h3>
            <p className="mt-1 text-sm text-slate-500">Active collection: <b>{activeColl?.name || "—"}</b>. When off, the seasonal modules are removed from the homepage and products page.</p>
          </div>
          {canManage ? (
            <button onClick={() => toggleProgram(!program.active)} data-testid="holiday-program-toggle" className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${program.active ? "bg-[#ED8B00]" : "bg-slate-300"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${program.active ? "left-6" : "left-1"}`} />
            </button>
          ) : <Badge color={program.active ? "green" : "slate"}>{program.active ? "Active" : "Inactive"}</Badge>}
        </div>
      )}

      {canManage && <HolidayZones />}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><Th>Request</Th><Th>Company</Th><Th>Mats</Th><Th>Assigned</Th><Th>Waiting</Th><Th>Date</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} onClick={() => setView(o)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" data-testid="holiday-row">
                <Td className="font-semibold text-slate-800">{o.reference}</Td>
                <Td><div className="font-medium text-slate-800">{o.customer?.company}</div><div className="text-xs text-slate-400">{o.customer?.contact_name}</div></Td>
                <Td>{o.total_mats}</Td>
                <Td>{o.assigned_to || "—"}</Td>
                <Td>{o.waiting_on && o.waiting_on !== "None" ? <Badge color="red">{o.waiting_on}</Badge> : "—"}</Td>
                <Td>{fmtDate(o.submitted_at || o.created_at)}</Td>
                <Td><Badge color={HOLIDAY_COLOR[o.status] || "slate"}>{o.status}</Badge></Td>
              </tr>
            ))}
            {orders.length === 0 && <tr><Td colSpan={7}><Empty>No holiday requests yet.</Empty></Td></tr>}
          </tbody>
        </table>
      </div>
      </>
      )}

      {view && <HolidayOrderDetail order={view} onChange={refreshView} onClose={() => setView(null)} />}
    </div>
  );
}

const REPORT_RANGES = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["365", "1 year"], ["0", "All time"]];

function StatTile({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-3xl font-extrabold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function BreakdownList({ title, data, testId }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <h3 className="font-heading font-bold text-slate-900">{title}</h3>
      <div className="mt-3 space-y-2">
        {entries.length === 0 && <Empty>No data.</Empty>}
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-3 text-sm">
            <span className="w-40 flex-shrink-0 truncate text-slate-600">{k}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-[#ED8B00]" style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className="w-8 flex-shrink-0 text-right font-semibold text-slate-800">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HolidayReporting() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    holiday.getHolidayReporting(Number(days)).then(setData).catch(() => setData(null));
  }, [days]);

  const rangeLabel = (REPORT_RANGES.find(([v]) => v === days) || [, "30 days"])[1];

  const exportCsv = () => {
    if (!data) return;
    const rows = [];
    const add = (metric, value) => rows.push({ metric, value });
    add("Date range", rangeLabel);
    add("Total orders", data.total_orders);
    add("Total mats", data.total_mats);
    add("Personalized orders", data.personalized_orders);
    add("Standard orders", data.standard_orders);
    add("Personalized mats", data.personalized_mats);
    add("Standard mats", data.standard_mats);
    add("", "");
    Object.entries(data.type_breakdown || {}).forEach(([k, v]) => add(`Personalization type: ${k}`, v));
    Object.entries(data.size_breakdown || {}).forEach(([k, v]) => add(`Mat size: ${k}`, v));
    Object.entries(data.status_counts || {}).forEach(([k, v]) => add(`Order status: ${k}`, v));
    Object.entries(data.artwork_status_counts || {}).forEach(([k, v]) => add(`Artwork: ${k}`, v));
    Object.entries(data.pipeline_stages || {}).forEach(([k, v]) => add(`Pipeline: ${k}`, v));
    add("", "");
    (data.popular_designs || []).forEach((d, i) => add(`Popular design #${i + 1}: ${d.name} (${d.number})`, d.count));
    downloadCsv("vangelder-holiday-report", [
      { key: "Metric", get: (r) => r.metric }, { key: "Value", get: (r) => r.value }], rows);
  };

  const printReport = () => {
    if (!data) return;
    const sect = (title, obj) => `<h3>${title}</h3><table>${Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => `<tr><td>${k}</td><td class="n">${v}</td></tr>`).join("")}</table>`;
    const designs = `<h3>Most Popular Designs</h3><table><tr><th>#</th><th>Design</th><th>Number</th><th class="n">Mats</th></tr>${(data.popular_designs || []).map((d, i) => `<tr><td>${i + 1}</td><td>${d.name}</td><td>${d.number}</td><td class="n">${d.count}</td></tr>`).join("")}</table>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print.");
    w.document.write(`<html><head><title>Holiday Mat Program Report</title><style>
      body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:32px;max-width:820px;margin:0 auto;}
      h1{color:#ED8B00;margin:0 0 2px;} .sub{color:#666;font-size:13px;margin-bottom:20px;}
      .tiles{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:8px;}
      .tile{border:1px solid #ddd;border-radius:8px;padding:12px 16px;min-width:130px;}
      .tile .v{font-size:26px;font-weight:800;} .tile .l{font-size:11px;text-transform:uppercase;color:#888;}
      h3{margin:20px 0 6px;font-size:14px;} table{width:100%;border-collapse:collapse;font-size:13px;}
      td,th{border-bottom:1px solid #eee;padding:5px 8px;text-align:left;} .n{text-align:right;font-weight:700;}
      @media print{button{display:none;}}</style></head><body>
      <h1>Holiday Mat Program — Reporting</h1>
      <div class="sub">Range: ${rangeLabel} · Generated ${new Date().toLocaleString()}</div>
      <div class="tiles">
        <div class="tile"><div class="v">${data.total_orders}</div><div class="l">Total Orders</div></div>
        <div class="tile"><div class="v">${data.total_mats}</div><div class="l">Total Mats</div></div>
        <div class="tile"><div class="v">${data.personalized_mats}</div><div class="l">Personalized Mats</div></div>
        <div class="tile"><div class="v">${data.standard_mats}</div><div class="l">Standard Mats</div></div>
      </div>
      ${sect("Personalization Type", data.type_breakdown)}
      ${sect("Mat Sizes", data.size_breakdown)}
      ${sect("Order Status", data.status_counts)}
      ${sect("Artwork Progress", data.artwork_status_counts)}
      ${sect("Pipeline", data.pipeline_stages)}
      ${designs}
      <button onclick="window.print()" style="margin-top:24px;padding:10px 18px;background:#ED8B00;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;">Print</button>
      </body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="holiday-reporting">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-500">Date range:</span>
        {REPORT_RANGES.map(([v, label]) => (
          <button key={v} onClick={() => setDays(v)} data-testid={`report-range-${v}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${days === v ? "bg-[#ED8B00] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
        ))}
        <div className="ml-auto flex gap-2">
          <Btn variant="outline" onClick={exportCsv} disabled={!data} data-testid="report-export-csv"><Download className="h-4 w-4" />Export CSV</Btn>
          <Btn variant="outline" onClick={printReport} disabled={!data} data-testid="report-print"><Printer className="h-4 w-4" />Print</Btn>
        </div>
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Total Orders" value={data.total_orders} sub={`${data.personalized_orders} personalized · ${data.standard_orders} standard`} testId="stat-total-orders" />
            <StatTile label="Total Mats" value={data.total_mats} sub={`${data.personalized_mats} personalized · ${data.standard_mats} standard`} testId="stat-total-mats" />
            <StatTile label="Personalized Mats" value={data.personalized_mats} sub={data.total_mats ? `${Math.round((data.personalized_mats / data.total_mats) * 100)}% of all mats` : "—"} testId="stat-personalized-mats" />
            <StatTile label="Standard Mats" value={data.standard_mats} sub={data.total_mats ? `${Math.round((data.standard_mats / data.total_mats) * 100)}% of all mats` : "—"} testId="stat-standard-mats" />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-4">
            {Object.entries(data.pipeline_stages || {}).map(([stage, count]) => (
              <div key={stage} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center" data-testid={`pipeline-${stage.toLowerCase()}`}>
                <p className="text-2xl font-extrabold text-slate-900">{count}</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stage}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <BreakdownList title="Personalization Type" data={data.type_breakdown} testId="report-type-breakdown" />
            <BreakdownList title="Mat Sizes" data={data.size_breakdown} testId="report-size-breakdown" />
            <BreakdownList title="Order Status" data={data.status_counts} testId="report-status-breakdown" />
            <BreakdownList title="Artwork Progress" data={data.artwork_status_counts} testId="report-artwork-breakdown" />
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="report-popular-designs">
            <h3 className="font-heading font-bold text-slate-900">Most Popular Designs</h3>
            <table className="mt-3 w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-400"><tr><Th>#</Th><Th>Design</Th><Th>Number</Th><Th>Mats</Th></tr></thead>
              <tbody>
                {(data.popular_designs || []).map((d, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <Td>{i + 1}</Td><Td className="font-medium text-slate-800">{d.name}</Td><Td>{d.number}</Td><Td className="font-semibold">{d.count}</Td>
                  </tr>
                ))}
                {(data.popular_designs || []).length === 0 && <tr><Td colSpan={4}><Empty>No data.</Empty></Td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ArtworkPanel({ order, index, personalization, onChange }) {
  const p = personalization || {};
  const [st, setSt] = useState(p.artwork_status || "Not Started");
  const [by, setBy] = useState(p.approved_by || "");
  const [at, setAt] = useState(p.approved_at || "");
  const [notes, setNotes] = useState(p.artwork_notes || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      onChange(await holiday.updateHolidayItemArtwork(order.id, index, {
        artwork_status: st, approved_by: by, approved_at: at, artwork_notes: notes }));
      toast.success("Artwork details saved.");
    } catch { toast.error("Could not save artwork details."); }
    setSaving(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { onChange(await holiday.uploadHolidayArtworkFile(order.id, index, file)); toast.success("Artwork file uploaded."); }
    catch { toast.error("Upload failed."); }
    setUploading(false);
    e.target.value = "";
  };
  const viewFile = async () => {
    try {
      const blob = await holiday.fetchHolidayArtworkBlob(p.artwork_file_url);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open file."); }
  };

  const AW_COLOR = { "Not Started": "slate", "In Progress": "orange", "Proof Sent": "blue", Approved: "green", "Revisions Requested": "red" };
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`artwork-panel-${index}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Artwork &amp; Approval</p>
        <Badge color={AW_COLOR[st] || "slate"}>{st}</Badge>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <div><label className="text-[10px] font-semibold uppercase text-slate-400">Status</label>
          <Select value={st} onChange={(e) => setSt(e.target.value)} data-testid={`artwork-status-${index}`}>
            {holiday.HOLIDAY_ARTWORK_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select></div>
        <div><label className="text-[10px] font-semibold uppercase text-slate-400">Approved By</label>
          <Input value={by} onChange={(e) => setBy(e.target.value)} placeholder="Name" data-testid={`artwork-approvedby-${index}`} /></div>
        <div><label className="text-[10px] font-semibold uppercase text-slate-400">Approval Date</label>
          <Input type="date" value={at} onChange={(e) => setAt(e.target.value)} data-testid={`artwork-approvedat-${index}`} /></div>
      </div>
      <div className="mt-2"><label className="text-[10px] font-semibold uppercase text-slate-400">Internal Artwork Notes</label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`artwork-notes-${index}`} /></div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Btn onClick={save} disabled={saving} data-testid={`artwork-save-${index}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Artwork Details"}</Btn>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid={`artwork-upload-label-${index}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 rotate-180" />} Upload Proof/Artwork
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" className="hidden" onChange={upload} data-testid={`artwork-file-input-${index}`} />
        </label>
        {p.artwork_file_url && <Btn variant="outline" onClick={viewFile} data-testid={`artwork-view-${index}`}>View: {p.artwork_file_name || "file"}</Btn>}
      </div>
    </div>
  );
}

function HolidayOrderDetail({ order, onChange, onClose }) {
  const c = order.customer || {};
  const setStatus = async (status) => { try { onChange(await holiday.setHolidayOrderStatus(order.id, status)); } catch { toast.error("Could not update status."); } };
  const patch = async (body) => { try { onChange(await holiday.updateHolidayOrder(order.id, body)); } catch { toast.error("Update failed."); } };
  const resend = async (which) => { try { onChange(await holiday.resendHolidayEmail(order.id, which)); toast.success("Resend attempted."); } catch { toast.error("Resend failed."); } };
  const cc = order.customer_confirmation || {}; const inn = order.internal_notification || {}; const art = order.art_notification || {};
  const [vgNum, setVgNum] = useState(order.vg_order_number || "");
  const saveVgNum = async () => { try { onChange(await holiday.setHolidayOrderNumber(order.id, vgNum.trim())); toast.success("vG order number saved."); } catch { toast.error("Could not save order number."); } };

  return (
    <Modal open onClose={onClose} title={`Holiday request · ${order.reference}`} wide testId="holiday-detail">
      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="vg-order-number-panel">
        <div className="flex-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">vG System Order # (archiving)</label>
          <Input value={vgNum} onChange={(e) => setVgNum(e.target.value)} placeholder="e.g. VG-10023" data-testid="vg-order-number-input" />
        </div>
        <Btn onClick={saveVgNum} data-testid="vg-order-number-save">Save</Btn>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={HOLIDAY_COLOR[order.status] || "slate"}>{order.status}</Badge>
        <span className="text-xs text-slate-400">{fmtDate(order.submitted_at || order.created_at)}</span>
        <span className="ml-auto flex items-center gap-2 text-sm">
          <span className="text-slate-500">Status</span>
          <Select value={order.status} onChange={(e) => setStatus(e.target.value)} data-testid="holiday-status-select">
            {holiday.HOLIDAY_ORDER_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </span>
      </div>

      <div className="mt-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3" data-testid="holiday-workflow">
        <div><label className="text-xs font-semibold uppercase text-slate-500">Assigned To</label>
          <Select value={order.assigned_to || "Unassigned"} onChange={(e) => patch({ assigned_to: e.target.value })} data-testid="holiday-assign-select">
            {holiday.HOLIDAY_ASSIGNEES.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select></div>
        <div><label className="text-xs font-semibold uppercase text-slate-500">Waiting On</label>
          <Select value={order.waiting_on || "None"} onChange={(e) => patch({ waiting_on: e.target.value })} data-testid="holiday-waiting-select">
            {holiday.HOLIDAY_WAITING.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select></div>
        <div><label className="text-xs font-semibold uppercase text-slate-500">Reason</label>
          <Select value={order.waiting_reason || ""} onChange={(e) => patch({ waiting_reason: e.target.value })} data-testid="holiday-reason-select">
            <option value="">—</option>{holiday.HOLIDAY_WAITING_REASONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select></div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid="holiday-email-states">
        <span>Customer confirmation: <Badge color={EMAIL_COLOR[cc.status] || "slate"}>{cc.status || "pending"}</Badge></span>
        <Btn variant="ghost" onClick={() => resend("customer")} data-testid="resend-customer">Resend</Btn>
        <span className="ml-2">Customer service: <Badge color={EMAIL_COLOR[inn.status] || "slate"}>{inn.status || "pending"}</Badge></span>
        <Btn variant="ghost" onClick={() => resend("internal")} data-testid="resend-internal">Resend</Btn>
        {art.status && art.status !== "skipped" && (<>
          <span className="ml-2">Art Department: <Badge color={EMAIL_COLOR[art.status] || "slate"}>{art.status}</Badge></span>
          <Btn variant="ghost" onClick={() => resend("art")} data-testid="resend-art">Resend</Btn>
        </>)}
      </div>

      <div className="mt-4 grid gap-x-6 sm:grid-cols-2">
        <DetailRow label="Customer type" value={CUSTOMER_TYPE_LABEL[c.customer_type] || c.customer_type} />
        <DetailRow label="Company" value={c.company} />
        <DetailRow label="Contact" value={c.contact_name} />
        <DetailRow label="Email" value={c.email} />
        <DetailRow label="Phone" value={c.phone} />
        <DetailRow label="Needed by (requested)" value={c.needed_by_date} />
        <DetailRow label="Collection" value={order.collection_name} />
        <DetailRow label="Entryway" value={order.entryway} />
        <DetailRow label="Billing address" value={c.billing_address} />
        <DetailRow label="Shipping address" value={c.ship_same ? "Same as billing address" : c.shipping_address} />
      </div>
      <DetailRow label="Notes / Special instructions" value={c.notes} />

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Request items ({order.total_mats} mats)</p>
        <div className="space-y-2">
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3" data-testid="holiday-item-row">
              <img src={it.image} alt="" className="h-12 w-12 flex-shrink-0 rounded object-cover" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">{it.personalization?.enabled ? it.design_name : `${it.design_number} · ${it.design_name}`}</p>
                <p className="text-xs text-slate-500">{it.size} · Qty {it.quantity} · <span className="font-semibold text-slate-700">{money(itemUnitPrice(it))} each</span> · <span className="font-semibold text-slate-700">{money(itemUnitPrice(it) * (it.quantity || 0))} total</span>{!it.personalization?.enabled && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">Standard</span>}</p>
                {it.personalization?.enabled && (
                  <div className="mt-1">
                    {it.personalization.type && <span className="mb-1 inline-block rounded bg-[#ED8B00]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#ED8B00]" data-testid="holiday-item-ptype">{it.personalization.type}</span>}
                    <p className="text-xs font-medium text-[#ED8B00]">Personalized: “{it.personalization.message}” · {it.personalization.font} · {it.personalization.color}</p>
                    {it.personalization.preview_number && <p className="mt-0.5 text-xs font-semibold text-slate-700">Customer Personalization Preview: {it.personalization.preview_number}</p>}
                    {it.personalization.preview_image_url && (
                      <a href={it.personalization.preview_image_url} target="_blank" rel="noreferrer" data-testid="holiday-preview-link">
                        <img src={it.personalization.preview_image_url} alt="Customer Personalization Preview" className="mt-1.5 w-full max-w-xs rounded-md border border-slate-200" data-testid="holiday-preview-image" />
                      </a>
                    )}
                    <ArtworkPanel order={order} index={i} personalization={it.personalization} onChange={onChange} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase text-slate-500">Estimated Total</span>
          <span className="text-lg font-extrabold" style={{ color: ORANGE }} data-testid="holiday-order-total">{money((order.items || []).reduce((n, it) => n + itemUnitPrice(it) * (it.quantity || 0), 0))}</span>
        </div>
      </div>

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Activity history</p>
        <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-3" data-testid="holiday-activity">
          {(order.activity || []).slice().reverse().map((a, i) => (
            <div key={i} className="flex gap-2 text-xs text-slate-600"><span className="whitespace-nowrap text-slate-400">{fmtDate(a.at)}</span><span>—</span><span>{a.action}{a.actor ? ` (${a.actor})` : ""}</span></div>
          ))}
          {(order.activity || []).length === 0 && <p className="text-xs text-slate-400">No activity yet.</p>}
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ Reps
function Reps({ refreshLists }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => crm.listReps().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loading />;
  const blank = { name: "", email: "", phone: "", title: "", region: "" };
  return (
    <div data-testid="view-reps">
      <PageTitle title="Representatives" action={<Btn onClick={() => setEdit(blank)} data-testid="rep-new"><Plus className="h-4 w-4" />Add rep</Btn>} />
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><Th>Name</Th><Th>Title</Th><Th>Region</Th><Th>Email</Th><Th>Phone</Th><Th></Th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid="rep-row">
                <Td className="font-semibold text-slate-800">{r.name}</Td><Td>{r.title || "—"}</Td><Td>{r.region || "—"}</Td><Td>{r.email || "—"}</Td><Td>{r.phone || "—"}</Td>
                <Td><div className="flex gap-2"><Btn variant="subtle" onClick={() => setEdit(r)}><Pencil className="h-3.5 w-3.5" /></Btn><Btn variant="danger" onClick={async () => { if (window.confirm("Delete rep?")) { await crm.deleteRep(r.id); load(); refreshLists(); } }}><Trash2 className="h-3.5 w-3.5" /></Btn></div></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><Td colSpan={6}><Empty>No representatives yet.</Empty></Td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <RepModal rep={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); refreshLists(); }} />}
    </div>
  );
}

function RepModal({ rep, onClose, onSaved }) {
  const isNew = !rep.id;
  const [f, setF] = useState(rep);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required.");
    setBusy(true);
    const body = { name: f.name, email: f.email, phone: f.phone, title: f.title, region: f.region };
    try { if (isNew) await crm.createRep(body); else await crm.updateRep(rep.id, body); toast.success("Saved."); onSaved(); }
    catch { toast.error("Save failed."); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? "Add representative" : "Edit representative"} testId="rep-modal">
      <div className="space-y-3">
        <Field label="Name"><Input value={f.name} onChange={set("name")} data-testid="rep-name" /></Field>
        <Field label="Title"><Input value={f.title} onChange={set("title")} /></Field>
        <Field label="Region"><Input value={f.region} onChange={set("region")} /></Field>
        <Field label="Email"><Input value={f.email} onChange={set("email")} /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set("phone")} /></Field>
      </div>
      <div className="mt-5 flex justify-end gap-2"><Btn variant="subtle" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} data-testid="rep-save">{busy ? "Saving…" : "Save"}</Btn></div>
    </Modal>
  );
}

// ------------------------------------------------------------------ Users
const ADMIN_ROLE_OPTIONS = [
  ["super_admin", "Super Admin — full control + tech support"],
  ["admin", "Admin — monitor all sections, manage team"],
  ["sales", "Sales — inquiries, pipeline & sample requests"],
  ["holiday", "Holiday — Holiday Mat Program only"],
];

function UsersView({ me }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales" });
  const canEdit = me?.role === "super_admin";
  const load = useCallback(() => crm.listUsers().then(setItems), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loading />;
  const create = async (e) => {
    e.preventDefault();
    try { await crm.createUser(form); setForm({ name: "", email: "", password: "", role: "sales" }); load(); toast.success("Team member added."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed."); }
  };
  const changeRole = async (u, role) => {
    try { await crm.updateUserRole(u.id, role); load(); toast.success(`${u.name} is now ${ROLE_LABEL[role]}.`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed."); }
  };
  const ROLE_BADGE = { super_admin: "orange", admin: "blue", sales: "green", holiday: "purple", member: "slate" };
  return (
    <div data-testid="view-users">
      <PageTitle title="Team & Roles" />
      {!canEdit && <p className="mb-4 rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-600" data-testid="users-readonly-note">You can view the team. Only a Super Admin can add members or change roles.</p>}
      <div className="grid gap-6 lg:grid-cols-3">
        {canEdit && (
        <form onSubmit={create} className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading font-bold text-slate-900">Add team member</h3>
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></Field>
          <Field label="Password"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} /></Field>
          <Field label="Role">
            <select data-testid="new-admin-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#ED8B00] focus:outline-none focus:ring-1 focus:ring-[#ED8B00]">
              {ADMIN_ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Btn type="submit" className="w-full">Add team member</Btn>
        </form>
        )}
        <div className={`${canEdit ? "lg:col-span-2" : "lg:col-span-3"} overflow-hidden rounded-xl border border-slate-200 bg-white`}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Added</Th><Th></Th></tr></thead>
            <tbody>
              {items.map((u) => {
                const isSelf = me && u.id === me.id;
                return (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-row-${u.id}`}>
                  <Td className="font-semibold text-slate-800">{u.name}{isSelf && <span className="ml-2 text-xs font-normal text-slate-400">(you)</span>}</Td>
                  <Td>{u.email}</Td>
                  <Td><Badge color={ROLE_BADGE[u.role] || "slate"}>{ROLE_LABEL[u.role] || u.role}</Badge></Td>
                  <Td>{fmtDate(u.created_at)}</Td>
                  <Td>
                    {canEdit && (
                    <div className="flex items-center gap-2">
                      <select data-testid={`role-select-${u.id}`} value={u.role} disabled={isSelf}
                        onChange={(e) => changeRole(u, e.target.value)}
                        title={isSelf ? "You cannot change your own role" : "Change role"}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs disabled:opacity-50">
                        {ADMIN_ROLE_OPTIONS.map(([v]) => <option key={v} value={v}>{ROLE_LABEL[v]}</option>)}
                      </select>
                      <Btn variant="danger" data-testid={`delete-admin-${u.id}`} disabled={isSelf} onClick={async () => { if (window.confirm("Delete team member?")) { try { await crm.deleteUser(u.id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } } }}><Trash2 className="h-3.5 w-3.5" /></Btn>
                    </div>
                    )}
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Shell
const SECTIONS = [
  { label: "Admin", roles: ["super_admin", "admin"], items: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "reps", label: "Reps", icon: Users },
    { key: "users", label: "Team & Roles", icon: UserCog },
  ] },
  { label: "Sales", roles: ["super_admin", "admin", "sales"], items: [
    { key: "inbox", label: "Inquiries", icon: Inbox },
    { key: "pipeline", label: "Pipeline", icon: KanbanSquare },
    { key: "companies", label: "Companies", icon: Building2 },
    { key: "orders", label: "Orders", icon: ShoppingCart },
  ] },
  { label: "Sample Requests", roles: ["super_admin", "admin", "sales"], items: [
    { key: "samples", label: "Sample Requests", icon: FlaskConical },
  ] },
  { label: "Holiday Mat Program", roles: ["super_admin", "admin", "holiday"], items: [
    { key: "holiday", label: "Holiday Requests", icon: Sparkles },
  ] },
];

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", sales: "Sales", holiday: "Holiday", member: "No Access" };

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState(null);
  const [reps, setReps] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [newCount, setNewCount] = useState(0);

  const refreshLists = useCallback(() => {
    crm.listReps().then(setReps).catch(() => {});
    crm.listCompanies().then(setCompanies).catch(() => {});
  }, []);

  const refreshInbox = useCallback(() => {
    crm.listSubmissions()
      .then((s) => setNewCount(s.filter((x) => x.status === "new" && x.form_type !== "request_samples").length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    fetchMe().then((u) => { setUser(u); }).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);
  useEffect(() => { if (user) refreshLists(); }, [user, refreshLists]);
  useEffect(() => {
    if (!user) return;
    refreshInbox();
    const t = setInterval(refreshInbox, 30000);
    return () => clearInterval(t);
  }, [user, refreshInbox]);

  useEffect(() => { document.title = "van Gelder CRM"; }, []);

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-slate-900"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;
  if (!user) return <LoginGate onAuthed={(u) => setUser(u)} />;
  if (user.must_change_password) return <ForcePasswordChange onDone={() => { clearToken(); setUser(null); }} />;

  const logout = () => { clearToken(); setUser(null); };
  const role = user.role;
  const isSuper = role === "super_admin";
  const canAdmin = ["super_admin", "admin"].includes(role);
  const canSales = ["super_admin", "admin", "sales"].includes(role);
  const canHoliday = ["super_admin", "admin", "holiday"].includes(role);

  const sections = SECTIONS.filter((s) => s.roles.includes(role));
  const accessibleKeys = sections.flatMap((s) => s.items.map((i) => i.key));
  const currentView = accessibleKeys.includes(view) ? view : accessibleKeys[0];

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-app">
      {/* Sidebar */}
      <aside className="flex w-56 flex-shrink-0 flex-col bg-slate-900 text-slate-300">
        <div className="border-b border-slate-800 px-5 py-5">
          <div className="h-1 w-10 rounded-full" style={{ backgroundColor: ORANGE }} />
          <h1 className="mt-2 font-heading text-lg font-extrabold text-white">van Gelder</h1>
          <p className="text-xs text-slate-400">CRM &amp; Admin</p>
        </div>
        <nav className="flex-1 space-y-4 overflow-y-auto p-3">
          {sections.map((sec) => (
            <div key={sec.label} data-testid={`nav-section-${sec.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{sec.label}</p>
              <div className="space-y-1">
                {sec.items.map((n) => (
                  <button key={n.key} onClick={() => setView(n.key)} data-testid={`nav-${n.key}`}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${currentView === n.key ? "bg-[#ED8B00] text-white" : "hover:bg-slate-800 hover:text-white"}`}>
                    <n.icon className="h-4 w-4" />
                    <span className="flex-1 text-left">{n.label}</span>
                    {n.key === "inbox" && newCount > 0 && (
                      <span data-testid="inbox-badge" className="ml-auto min-w-[1.25rem] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-xs font-bold text-white">
                        {newCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="px-2 pb-2 text-xs text-slate-400" data-testid="admin-identity">
            {user.name}<br />
            <span className="text-slate-500">{user.email}</span>
            <span className="mt-1 inline-block rounded-full bg-[#ED8B00] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-testid="admin-role-badge">
              {ROLE_LABEL[role] || role}
            </span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white">
            <LogOut className="h-4 w-4" />Sign out
          </button>
        </div>
      </aside>
      {/* Content */}
      <main className="flex-1 overflow-x-hidden p-6 lg:p-8">
        {currentView === "dashboard" && canAdmin && <Dashboard reps={reps} canManage={canAdmin} />}
        {currentView === "inbox" && canSales && <InboxView reps={reps} companies={companies} refreshLists={refreshLists} onCountChange={refreshInbox} kind="inquiries" />}
        {currentView === "samples" && canSales && <InboxView reps={reps} companies={companies} refreshLists={refreshLists} onCountChange={refreshInbox} kind="samples" />}
        {currentView === "pipeline" && canSales && <Pipeline reps={reps} companies={companies} />}
        {currentView === "companies" && canSales && <Companies reps={reps} refreshLists={refreshLists} />}
        {currentView === "orders" && canSales && <Orders reps={reps} companies={companies} />}
        {currentView === "holiday" && canHoliday && <HolidayOrders canManage={canHoliday} />}
        {currentView === "reps" && canAdmin && <Reps refreshLists={refreshLists} />}
        {currentView === "users" && canAdmin && <UsersView me={user} />}
      </main>
    </div>
  );
}

// ------------------------------------------------------------------ small helpers
function useNameMap(list) {
  return useMemo(() => {
    const m = {};
    (list || []).forEach((x) => { m[x.id] = x.name; });
    return (id) => m[id] || "";
  }, [list]);
}
const PageTitle = ({ title, action }) => (
  <div className="mb-5 flex items-center justify-between">
    <h2 className="font-heading text-2xl font-extrabold text-slate-900">{title}</h2>
    {action}
  </div>
);
const ExportBtn = ({ onClick, disabled, testId }) => (
  <Btn variant="outline" onClick={onClick} disabled={disabled} data-testid={testId}>
    <Download className="h-4 w-4" />Export CSV
  </Btn>
);
const Th = ({ children }) => <th className="px-4 py-2.5 font-semibold">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-4 py-3 align-top text-slate-600 ${className}`}>{children}</td>;
const Loading = () => <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
const Empty = ({ children }) => <div className="py-6 text-center text-sm text-slate-400">{children}</div>;
const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 p-3 text-center"><p className="text-xs text-slate-500">{label}</p><p className="mt-1 text-xl font-bold text-slate-800">{value}</p></div>
);
const Panel = ({ title, children }) => (
  <div className="rounded-lg border border-slate-200 p-3"><p className="mb-1 text-xs font-semibold uppercase text-slate-500">{title}</p>{children}</div>
);
